import dayjs from 'dayjs';
import type { PipelineStatusKey } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { MiniMetricChartSummary } from '@/features/shared/components/chartPanels/MiniMetricChartHeader';
import type {
  MetricsStatusChartPanelDisplayModel,
  MetricsStatusChartPanelModelToolbarState,
} from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';

type PipelineMetricItemId = 'passRate' | 'frequency' | 'avgDuration';

const mapMetricValueTone = (
  tone: MetricValueItem['valueTone'],
): MiniMetricChartSummary['valueTone'] => {
  switch (tone) {
    case 'default':
    case 'neutral':
    case 'success':
    case 'green':
      return 'green';
    case 'error':
    case 'red':
      return 'red';
    case 'warning':
    case 'orange':
    case 'yellow':
      return 'orange';
    case 'brown':
      return 'brown';
    case 'info':
    case 'blue':
      return 'blue';
    case 'primary':
    case 'teal':
      return 'teal';
    case 'pink':
    case 'grey':
    case 'transparent':
      return 'neutral';
    case 'purple':
      return 'purple';
  }
};

export const toMiniMetricSummary = (
  item: MetricValueItem | null,
): MiniMetricChartSummary | null => {
  if (!item) return null;
  return {
    valueLabel: item.valueLabel,
    valueTone: mapMetricValueTone(item.valueTone),
    deltaLabel: item.deltaLabel ?? null,
    deltaValue: item.deltaLabel
      ? item.deltaLabel.startsWith('+')
        ? 1
        : item.deltaLabel.startsWith('-')
          ? -1
          : 0
      : null,
    deltaTone: item.deltaTone,
    isMissing: item.valueLabel === '-',
  };
};

export const buildPipelinesToolbarState = ({
  search,
  setSearch,
  selectedStatuses,
  setSelectedStatuses,
  selectedTags,
  setSelectedTags,
  noRowsText,
  dateQuickFilter,
  onDateQuickFilterChange,
  startDay,
  endDay,
  onFromDateChange,
  onToDateChange,
}: {
  search: string;
  setSearch: (value: string) => void;
  selectedStatuses: PipelineStatusKey[];
  setSelectedStatuses: (value: PipelineStatusKey[]) => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  noRowsText: string;
  dateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  onDateQuickFilterChange: (value: '7d' | '14d' | '30d' | 'latest') => void;
  startDay: string | null;
  endDay: string | null;
  onFromDateChange: (value: string | null) => void;
  onToDateChange: (value: string | null) => void;
}): MetricsStatusChartPanelModelToolbarState<PipelineStatusKey> => ({
  search,
  setSearch,
  selectedStatuses,
  setSelectedStatuses,
  selectedTags,
  setSelectedTags,
  noRowsText,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate: startDay ? dayjs(startDay) : null,
  toDate: endDay ? dayjs(endDay) : null,
  onFromDateChange,
  onToDateChange,
});

export const buildPipelinesChartModelOverride = ({
  stackedChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  metricValueItems,
}: {
  stackedChartData: MetricsStatusChartPanelDisplayModel['statusChartData'];
  passRateChartData: MetricsStatusChartPanelDisplayModel['passRateChartData'];
  frequencyChartData: MetricsStatusChartPanelDisplayModel['frequencyChartData'];
  durationChartData: MetricsStatusChartPanelDisplayModel['durationChartData'];
  metricValueItems: MetricValueItem[];
}): MetricsStatusChartPanelDisplayModel => {
  const metricItemsById = Object.fromEntries(
    metricValueItems.map((item) => [item.id, item]),
  ) as Partial<Record<PipelineMetricItemId, MetricValueItem>>;

  return {
    statusChartData: stackedChartData,
    passRateChartData,
    frequencyChartData,
    durationChartData,
    showStatusChartSkeleton: false,
    passRateSummary: toMiniMetricSummary(metricItemsById.passRate ?? null),
    frequencySummary: toMiniMetricSummary(metricItemsById.frequency ?? null),
    durationSummary: toMiniMetricSummary(metricItemsById.avgDuration ?? null),
  };
};
